/**
 * Suppress known noisy third-party requests and errors.
 *
 * Why:
 * - Spline runtime sometimes tries to fetch a remote config.json from a Netlify URL.
 * - That URL can 404 and/or fail CORS, spamming the console.
 * - SoundCloud widget embeds throw generic "Script error" CORS exceptions that can't be fixed.
 * - SoundCloud widgets also throw IndexSizeError from canvas operations (harmless).
 *
 * This shim intercepts specific requests and suppresses harmless third-party console errors,
 * avoiding console noise while keeping functionality working.
 */
export function suppressThirdPartyNoise() {
  if (typeof window === "undefined") return;

  // Suppress Spline config.json fetch
  if (typeof window.fetch === "function") {
    const BLOCKED_CONFIG_URL = "https://effulgent-jelly-3cd660.netlify.app/config.json";

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
      try {
        const url = typeof input === "string" ? input : input?.url;
        if (url === BLOCKED_CONFIG_URL) {
          return new Response("{}", {
            status: 200,
            headers: { "Content-Type": "application/json" },
          });
        }
      } catch {
        // fall through to real fetch
      }

      return originalFetch(input, init);
    };
  }

  // Check if error is from SoundCloud widget (harmless third-party errors)
  const isSoundCloudWidgetError = (error) => {
    if (!error) return false;
    
    const errorMessage = error.message || error.toString() || "";
    const errorName = error.name || "";
    const errorStack = error.stack || "";
    
    // Check for SoundCloud widget indicators
    const hasWidgetIndicator = 
      errorMessage.includes("widget-") ||
      errorStack.includes("widget-") ||
      errorMessage.includes("SoundCloud") ||
      errorStack.includes("SoundCloud");
    
    // Check for specific harmless errors
    const isHarmlessError =
      // Script error exceptions (CORS-related)
      errorMessage.includes("Script error") ||
      errorMessage.includes("Exception: Script error") ||
      // Canvas IndexSizeError (harmless - happens when widget tries to process image before it loads)
      (errorName === "IndexSizeError" && errorMessage.includes("getImageData")) ||
      (errorName === "IndexSizeError" && errorMessage.includes("CanvasRenderingContext2D")) ||
      // Generic widget errors
      (hasWidgetIndicator && (errorMessage.includes("Exception") || errorName === "IndexSizeError"));
    
    return isHarmlessError;
  };

  // Suppress SoundCloud widget console.error calls ONLY
  // This only silences console noise, doesn't affect widget functionality
  const originalConsoleError = console.error;
  console.error = function (...args) {
    // Check if any argument is a SoundCloud widget error
    const hasSoundCloudError = args.some(arg => {
      if (arg instanceof Error) {
        return isSoundCloudWidgetError(arg);
      }
      const argStr = String(arg);
      return (
        argStr.includes("widget-") &&
        (argStr.includes("Exception: Script error") ||
         argStr.includes("IndexSizeError") ||
         argStr.includes("getImageData") ||
         argStr.includes("SoundCloud"))
      );
    });
    
    if (hasSoundCloudError) {
      // Only suppress console output - widgets still function normally
      return;
    }
    
    // Pass through all other errors
    originalConsoleError.apply(console, args);
  };

  // Note: We do NOT suppress window.onerror or unhandledrejection
  // This ensures widgets can still handle their own errors internally
  // We only silence the console noise, not actual error handling
}


